import type { RefObject } from 'react'

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from '@/components/ui/sheet'

const HOKU_INTAKE_HOSTNAMES = new Set([
  'app.careforce.ai',
  'hoku.careforce.ai',
  'hoku-preview.careforce.ai',
  'hoku-staging.careforce.ai',
])
const LOCAL_HOSTNAMES = new Set(['127.0.0.1', 'localhost'])
const MONO_INTAKE_ORIGIN = 'https://mono.careforce.ai'

export function isAllowedIntakeUrl(intakeUrl: URL): boolean {
  if (LOCAL_HOSTNAMES.has(window.location.hostname)) {
    return (
      intakeUrl.protocol === 'http:' &&
      LOCAL_HOSTNAMES.has(intakeUrl.hostname) &&
      intakeUrl.port === '8082'
    )
  }
  return (
    window.location.protocol === 'https:' &&
    HOKU_INTAKE_HOSTNAMES.has(window.location.hostname) &&
    intakeUrl.origin === MONO_INTAKE_ORIGIN
  )
}

interface CredentialIntakeSheetProps {
  open: boolean
  targetName: string | null
  isLaunching: boolean
  iframeRef: RefObject<HTMLIFrameElement | null>
  onOpenChange: (open: boolean) => void
}

/* oxlint-disable react/iframe-missing-sandbox -- Mono is required to be cross-origin; scripts and origin semantics are both needed for its session-bound form. */
export function CredentialIntakeSheet({
  open,
  targetName,
  isLaunching,
  iframeRef,
  onOpenChange,
}: CredentialIntakeSheetProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid="credential-intake-sheet"
        showCloseButton={false}
        onOpenAutoFocus={(event) => event.preventDefault()}
        className="bg-neutral-ui-bg w-full max-w-none gap-0 overflow-hidden rounded-l-xl p-0 sm:max-w-lg">
        <SheetTitle className="sr-only">
          Add eClinicalWorks credentials
        </SheetTitle>
        <SheetDescription className="sr-only">
          Secure credential entry
        </SheetDescription>
        <div className="relative min-h-0 flex-1 bg-muted/30">
          {targetName && (
            <iframe
              ref={iframeRef}
              title="Secure eClinicalWorks credential form"
              name={targetName}
              sandbox="allow-forms allow-same-origin allow-scripts"
              referrerPolicy="no-referrer"
              allow="camera 'none'; geolocation 'none'; microphone 'none'"
              className="h-full w-full border-0 bg-white"
            />
          )}
          {isLaunching && (
            <div
              role="status"
              className="absolute inset-0 grid place-items-center bg-background text-sm text-muted-foreground">
              Opening secure form…
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
/* oxlint-enable react/iframe-missing-sandbox */
